/**
 * Account Role Utilities (Frontend)
 *
 * Determines user roles from membership tables (canonical source).
 */

import { supabase } from '../supabase'
import type { UserRole } from '@/types/account'

export type { UserRole }

export type OrgMembershipRole = 'owner' | 'admin' | 'member'

/**
 * Resolve route role from active org memberships.
 * Plan assignment is not required — lead orgs may have null plan_id.
 */
export function resolveRoleFromOrgMemberships(
  memberships: Array<{ role: OrgMembershipRole }>,
): Exclude<UserRole, 'admin'> {
  if (memberships.length === 0) return 'individual'

  // Check all memberships — return highest privilege found
  for (const membership of memberships) {
    if (membership.role === 'owner' || membership.role === 'admin') {
      return 'organization_owner'
    }
  }

  // Has memberships but none are owner/admin
  return 'organization_member'
}

/**
 * Get the canonical role for an account by checking membership tables.
 *
 * Active membership is enough — plan_id is optional (lead orgs may have none).
 * Gating workspace features by plan belongs in entitlement checks, not role.
 */
export async function getUserRole(accountId: string): Promise<UserRole | null> {
  try {
    const { data: account } = await supabase
      .from('accounts')
      .select('is_admin')
      .eq('id', accountId)
      .single()

    if (account?.is_admin) return 'admin'

    const { data: memberships, error } = await supabase
      .from('partner_organization_members')
      .select('role')
      .eq('account_id', accountId)
      .eq('status', 'active')

    if (error) {
      console.error('[account-roles] org membership lookup failed:', error)
      return 'individual'
    }

    return resolveRoleFromOrgMemberships((memberships ?? []) as Array<{ role: OrgMembershipRole }>)
  } catch (error) {
    console.error('[account-roles] Error getting user role:', error)
    return null
  }
}

export function isAdminRole(role?: UserRole | null): boolean {
  return role === 'admin'
}

export function isOrganizationOwnerRole(role?: UserRole | null): boolean {
  return role === 'organization_owner'
}

export function isOrganizationMemberRole(role?: UserRole | null): boolean {
  return role === 'organization_owner' || role === 'organization_member'
}
